import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { compareDesc } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { withFragments } from '@sorare/core/src/lib/gql';
import { positionNames } from '@sorare/core/src/lib/players';

import type { Options } from '@sorare/marketplace/src/lib/performance';
import {
  ALL_COMPETITIONS_ITEM,
  ALL_TEAMS_ITEM,
  FilterKey,
  homeAwayOptions,
} from '@sorare/marketplace/src/lib/performance';

import type { usePerformanceFiltersOptions_anyPlayer } from './__generated__/usePerformanceFiltersOptions.graphql';

type Props = {
  player: usePerformanceFiltersOptions_anyPlayer;
};

type ClubMembership = NonNullable<
  NonNullable<usePerformanceFiltersOptions_anyPlayer['clubMemberships']>[number]
>;

type Competition = NonNullable<
  NonNullable<ClubMembership['club']>['activeCompetitions']
>[number];

const getSortedUniqueClubs = (clubMemberships: ClubMembership[]) =>
  clubMemberships
    .reduce<ClubMembership[]>((acc, cm) => {
      if (!acc.find(c => c?.club?.slug === cm?.club?.slug)) {
        acc.push(cm);
      }
      return acc;
    }, [])
    .filter(Boolean)
    .sort((a, b) =>
      a.startDate && b.startDate ? compareDesc(a.startDate, b.startDate) : 0
    )
    .map(cm => cm.club)
    .filter(Boolean);

export const usePerformanceFiltersOptions = withFragments(
  ({ player }: Props): Options => {
    const { activeNationalTeam, clubMemberships } = player;

    const deduplicatedClubs = getSortedUniqueClubs(clubMemberships);
    const [currentClub, ...otherClubs] = deduplicatedClubs;
    const teams = [
      ...(currentClub ? [currentClub] : []),
      ...(activeNationalTeam ? [activeNationalTeam] : []),
      ...otherClubs,
    ].filter(Boolean);

    const teamsOptions = [
      ALL_TEAMS_ITEM,
      ...teams.map(team => ({
        value: team.slug,
        label: (
          <LabelM bold>
            <Horizontal gap={1} as="span">
              <TeamAvatar size={16} team={team} />
              {team.shortName}
            </Horizontal>
          </LabelM>
        ),
      })),
    ];

    const activeCompetitions = clubMemberships
      .flatMap(team =>
        team?.club?.activeCompetitions?.filter(c => c.openForGameStats)
      )
      .filter(Boolean)
      .reduce<Competition[]>((acc, competition) => {
        if (!acc.find(c => c.slug === competition.slug)) {
          acc.push(competition);
        }
        return acc;
      }, [])
      ?.filter(Boolean);

    const nationalTeamCompetitions = activeNationalTeam?.activeCompetitions
      ?.filter(c => c.openForGameStats)
      .filter(Boolean);

    const activeCompetitionsOptions = [
      ALL_COMPETITIONS_ITEM,
      ...[
        ...(activeCompetitions || []),
        ...(nationalTeamCompetitions || []),
      ].map(c => ({
        value: c.slug,
        label: <LabelM bold>{c.displayName}</LabelM>,
      })),
    ];

    return {
      [FilterKey.TEAM]: teamsOptions,
      [FilterKey.COMPETITION]: activeCompetitionsOptions,
      [FilterKey.HOME_AWAY]: homeAwayOptions,
      [FilterKey.POSITION]:
        player.sport === Sport.FOOTBALL
          ? player.cardPositions.map(position => ({
              value: position,
              label: <FormattedMessage {...positionNames[position]} />,
            }))
          : [],
      [FilterKey.MODE]: [],
    };
  },
  {
    anyPlayer: gql`
      fragment usePerformanceFiltersOptions_anyPlayer on AnyPlayerInterface {
        slug
        cardPositions
        sport
        clubMemberships {
          id
          startDate
          club {
            slug
            name
            shortName
            activeCompetitions {
              slug
              displayName
              openForGameStats
            }
            ...TeamAvatar_team
          }
        }
        activeNationalTeam {
          slug
          name
          shortName
          ...TeamAvatar_team
          activeCompetitions {
            slug
            displayName
            openForGameStats
          }
        }
      }
      ${TeamAvatar.fragments.team}
    ` as TypedDocumentNode<usePerformanceFiltersOptions_anyPlayer>,
  }
);
